import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'
import { MainNavigation } from '../components/navigation/MainNavigation'
import { MainFooter } from '../components/navigation/MainFooter'
import { useAuthStore } from '../stores/authStore'
import { ServiceConfigUtils } from '../config/services.utils'
import { NAVIGATION_ACTIONS } from '../config/navigation.config'
import { 
  Brain, 
  Zap, 
  Users,
  TrendingUp,
  Shield,
  CheckCircle,
  Target
} from 'lucide-react'

export const LandingPage: React.FC = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()

  const handleGetStarted = () => {
    navigate(NAVIGATION_ACTIONS.primary.path)
  }

  const stats = [
    { number: '10x', label: 'Faster Delivery' },
    { number: '60%', label: 'Cost Savings' },
    { number: '427', label: 'Sources Analyzed' }
  ]

  // Generate services from configuration (including coming soon)
  const activeServices = ServiceConfigUtils.getActiveServices()
    .map(service => ServiceConfigUtils.toLandingPageFormat(service))
  const comingSoonServices = ServiceConfigUtils.getComingSoonServices()
    .map(service => ServiceConfigUtils.toLandingPageFormat(service))
  const services = [...activeServices, ...comingSoonServices]

  const qualities = [
    {
      icon: <Brain className="h-8 w-8 text-white" />,
      title: 'Research Strategy Mastery',
      description: 'Expert-designed investigation frameworks that ask the right questions and uncover hidden market dynamics others miss.'
    },
    {
      icon: <Zap className="h-8 w-8 text-white" />,
      title: 'AI-Orchestrated Efficiency',
      description: 'Advanced AI agents handle data collection, analysis, and quality control - delivering comprehensive insights 10x faster than traditional methods.'
    },
    {
      icon: <Target className="h-8 w-8 text-white" />,
      title: 'Strategic Synthesis',
      description: 'Human expertise transforms raw intelligence into actionable strategic recommendations that drive real business outcomes.'
    },
    {
      icon: <Users className="h-8 w-8 text-white" />,
      title: 'Client-Centric Approach',
      description: 'Deep understanding of your business context ensures every insight is relevant, practical, and aligned with your strategic objectives.'
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-white" />,
      title: 'Industry Specialization',
      description: 'Focused expertise in key sectors enables deeper insights and more accurate predictions than generalist consulting approaches.'
    },
    {
      icon: <Shield className="h-8 w-8 text-white" />,
      title: 'Proven Methodology',
      description: 'Battle-tested research frameworks and analysis protocols ensure consistent, high-quality intelligence delivery every time.'
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Navigation */}
      <MainNavigation variant="full" />

      {/* Hero Section */}
      <section className="relative py-20 px-4 text-center">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 to-transparent" />
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="mb-6">
            <div className="text-6xl md:text-7xl font-bold text-white mb-4">
              Strat<span className="text-blue-400">IQ</span><span className="text-cyan-400">X</span>
            </div>
            <div className="text-xl text-blue-200 font-medium mb-2">
              Strategic Intelligence Amplified
            </div>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 bg-gradient-to-r from-white to-blue-300 bg-clip-text text-transparent">
            AI-Powered Strategic Intelligence
          </h1>
          <p className="text-xl text-blue-200 mb-4 font-medium">
            Transforming Data Into Competitive Advantage
          </p>
          <p className="text-lg text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            Get comprehensive market intelligence and strategic insights delivered 10x faster than traditional consulting, 
            at 60% less cost, with human expertise guiding every analysis.
          </p>
          
          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-12 mb-12">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-cyan-400 mb-2">
                  {stat.number}
                </div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          <Button 
            onClick={handleGetStarted}
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-8 py-4 text-lg shadow-xl"
          >
            Explore Strategic Intelligence
          </Button>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 bg-slate-800/50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              StratIQX Intelligence Services
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Comprehensive market analysis and strategic insights powered by AI orchestration and human expertise
            </p>
            <div className="mt-6 inline-flex items-center bg-amber-900/20 border border-amber-500/30 rounded-lg px-4 py-2">
              <div className="w-2 h-2 bg-amber-400 rounded-full mr-3 animate-pulse"></div>
              <p className="text-amber-200 font-medium">
                Currently accepting 50 new clients per month to ensure quality delivery
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} className={`bg-slate-800 border-blue-800/30 transition-all duration-300 group relative ${
                service.comingSoon ? 'opacity-75' : 'hover:border-cyan-500/50'
              }`}>
                <CardContent className="p-8">
                  {/* Coming Soon Badge */}
                  {service.comingSoon && (
                    <div className="absolute top-4 right-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                      Coming Soon
                    </div>
                  )}
                  {/* Popular Badge */}
                  {service.popular && !service.comingSoon && (
                    <div className="absolute top-4 right-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                      Popular
                    </div>
                  )}
                  <div className="bg-gradient-to-r from-blue-600 to-cyan-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {service.title}
                  </h3>
                  <p className="text-gray-300 mb-4 leading-relaxed">
                    {service.description}
                  </p>
                  <div className={`text-lg font-semibold mb-4 ${
                    service.price.includes('Free') ? 'text-green-400' : 'text-cyan-400'
                  }`}>
                    {service.price}
                  </div>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-gray-300 text-sm">
                        <CheckCircle className="h-4 w-4 text-cyan-400 mr-3 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Qualities Section */}
      <section id="approach" className="py-20 px-4 bg-slate-900">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Why StratIQX Delivers Superior Intelligence
            </h2>
            <p className="text-lg text-gray-300">
              The unique capabilities that set our strategic intelligence apart
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {qualities.map((quality, index) => (
              <div key={index} className="text-center group">
                <div className="bg-gradient-to-r from-blue-600 to-cyan-600 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg">
                  {quality.icon}
                </div>
                <h3 className="text-xl font-semibold text-cyan-400 mb-4">
                  {quality.title}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {quality.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <MainFooter variant="full" />
    </div>
  )
}